import {
    IonAlert,
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonNote,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSelect,
    IonSelectOption,
    IonSpinner,
    IonText,
    IonTitle,
    IonToolbar,
    RefresherEventDetail,
    useIonRouter
} from "@ionic/react";

import {
    createOutline,
    peopleOutline,
    saveOutline,
    shieldCheckmarkOutline,
    swapHorizontalOutline
} from "ionicons/icons";

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Cookies from "js-cookie";

import "./FantasyCommissionerTeamSettings.css";

interface FantasyTeam {
    id: string;
    manager: string;
    teamName1: string;
    user: string;
}

interface LeagueInfo {
    teams?: string;
    maxTeams?: string;
    draftStartsAt?: string;
    date?: string;
    hour?: string;
}

interface LeagueDraftState {
    status?: string;
}

interface LeagueData {
    Info?: LeagueInfo;
    Draft?: unknown[];
    DraftState?: LeagueDraftState;
    Teams?: Record<string, FantasyTeam>;
}

interface SelectedTeam {
    id: string;
    manager: string;
    teamName: string;
    role: string;
}

const MIN_LEAGUE_TEAMS = 4;
const MAX_LEAGUE_TEAMS = 8;
const FANTASY_DATA_URL = "/fantasy_data";
const COMMISSIONER_CONTROL_URL =
    "/create_league/control_CommissionerTeam.php";

function isCommissionerRole(role: string): boolean {
    const normalizedRole = role.toLowerCase().trim();

    return normalizedRole === "comisioner" ||
        normalizedRole === "commissioner";
}

function hasDraftAlreadyStarted(league: LeagueData): boolean {
    const status = String(
        league.DraftState?.status || ""
    )
        .trim()
        .toLowerCase();

    if (
        status === "active" ||
        status === "completed" ||
        status === "cancelled"
    ) {
        return true;
    }

    if (
        Array.isArray(league.Draft) &&
        league.Draft.length > 0
    ) {
        return true;
    }

    const storedStart = String(
        league.Info?.draftStartsAt || ""
    ).trim();

    if (storedStart) {
        const startDate = new Date(
            storedStart.replace(" ", "T")
        );

        if (!Number.isNaN(startDate.getTime())) {
            return new Date() >= startDate;
        }
    }

    return false;
}

const FantasyCommissionerTeamSettings: React.FC = () => {
    const router = useIonRouter();
    const { id: leagueId } = useParams<{ id: string }>();

    const [teams, setTeams] = useState<FantasyTeam[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const [selectedTeam, setSelectedTeam] =
        useState<SelectedTeam | null>(null);

    const [editName, setEditName] = useState("");
    const [newOwnerId, setNewOwnerId] = useState("");
    const [newManagerName, setNewManagerName] = useState("");

    const [selectedRole, setSelectedRole] = useState<"user" | "comisioner">(
    "user"
);

const [showRoleAlert, setShowRoleAlert] = useState(false);

const [leagueInfo, setLeagueInfo] =
    useState<LeagueInfo | null>(null);

const [draftHasStarted, setDraftHasStarted] =
    useState(false);

const [configuredTeams, setConfiguredTeams] =
    useState("4");

const [maxTeams, setMaxTeams] =
    useState(8);

const [showParticipantsAlert, setShowParticipantsAlert] =
    useState(false);

    const [showReplaceAlert, setShowReplaceAlert] =
        useState(false);

    function getAccessToken(): string {
        return Cookies.get("access_token") || "";
    }

    function isAuthenticated(): boolean {
        return Boolean(
            Cookies.get("access_token") &&
            Cookies.get("access_username")
        );
    }

    const loadTeams = useCallback(
        async function (forceRefresh = false) {
            if (!isAuthenticated()) {
                router.push("/home", "root", "replace");
                return;
            }

            try {
                setError("");

                if (!forceRefresh) {
                    setLoading(true);
                }

                const response = await fetch(
    `${FANTASY_DATA_URL}/${encodeURIComponent(
        leagueId
    )}.json?t=${Date.now()}`,
    {
        cache: "no-store"
    }
);

                if (!response.ok) {
                    throw new Error("No fue posible cargar los equipos.");
                }

                const league = (await response.json()) as LeagueData;
                
                const currentUserId = getAccessToken();

                const currentUserRole =
                    league.Teams?.[currentUserId]?.user || "";

                const registeredTeams = Object.keys(
                    league.Teams || {}
                ).length;

                const minimumAllowedTeams = Math.max(
    MIN_LEAGUE_TEAMS,
    registeredTeams
);

const storedConfiguredTeams = Number(
    league.Info?.teams
);

const configuredTotal = Math.min(
    MAX_LEAGUE_TEAMS,
    Math.max(
        minimumAllowedTeams,
        Number.isFinite(storedConfiguredTeams)
            ? storedConfiguredTeams
            : minimumAllowedTeams
    )
);

setLeagueInfo(league.Info || null);
setDraftHasStarted(hasDraftAlreadyStarted(league));
setConfiguredTeams(String(configuredTotal));
setMaxTeams(MAX_LEAGUE_TEAMS);

                if (!isCommissionerRole(currentUserRole)) {
                    router.push(
                        `/fantasySettings/${leagueId}`,
                        "root",
                        "replace"
                    );

                    return;
                }

                const loadedTeams: FantasyTeam[] = Object.entries(
                    league.Teams || {}
                ).map(function ([teamId, team]) {
                    return {
                        id: teamId,
                        manager: team.manager || "Sin manager",
                        teamName1: team.teamName1 || "Equipo sin nombre",
                        user: team.user || "user"
                    };
                });

                loadedTeams.sort(function (firstTeam, secondTeam) {
                    return firstTeam.teamName1.localeCompare(
                        secondTeam.teamName1,
                        "es"
                    );
                });

                setTeams(loadedTeams);
            } catch (requestError) {
                console.error(
                    "No fue posible cargar los equipos:",
                    requestError
                );

                setError(
                    requestError instanceof Error
                        ? requestError.message
                        : "No fue posible cargar los equipos."
                );
            } finally {
                setLoading(false);
            }
        },
        [leagueId, router]
    );

    useEffect(
        function () {
            loadTeams();
        },
        [loadTeams]
    );

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadTeams(true);
        event.detail.complete();
    }

    function selectTeam(team: FantasyTeam) {
        setSelectedTeam({
            id: team.id,
            manager: team.manager,
            teamName: team.teamName1,
            role: team.user
        });

        setEditName(team.teamName1);
        setNewOwnerId("");
        setNewManagerName("");
        setSelectedRole(
    isCommissionerRole(team.user)
        ? "comisioner"
        : "user"
);

        setMessage("");
    }

function closeEditor() {
    setSelectedTeam(null);
    setEditName("");
    setNewOwnerId("");
    setNewManagerName("");
    setSelectedRole("user");
}

function requestParticipantsChange() {
    if (saving || draftHasStarted) {
        return;
    }

    const requestedTeams = Number(configuredTeams);

    const minimumAllowedTeams = Math.max(
        MIN_LEAGUE_TEAMS,
        teams.length
    );

    if (
        !Number.isInteger(requestedTeams) ||
        requestedTeams < minimumAllowedTeams
    ) {
        setMessage(
            `La liga debe tener al menos ${minimumAllowedTeams} participantes.`
        );

        return;
    }

    if (requestedTeams > MAX_LEAGUE_TEAMS) {
        setMessage(
            `El máximo permitido para una liga es ${MAX_LEAGUE_TEAMS} participantes.`
        );

        return;
    }

    setShowParticipantsAlert(true);
}

async function saveParticipantsChange() {
    if (saving || draftHasStarted) {
        return;
    }

    const totalTeams = Number(configuredTeams);

    const minimumAllowedTeams = Math.max(
        MIN_LEAGUE_TEAMS,
        teams.length
    );

    if (
        !Number.isInteger(totalTeams) ||
        totalTeams < minimumAllowedTeams ||
        totalTeams > MAX_LEAGUE_TEAMS
    ) {
        setMessage(
            `Selecciona entre ${minimumAllowedTeams} y ${MAX_LEAGUE_TEAMS} participantes.`
        );

        return;
    }

    try {
        setSaving(true);
        setMessage("");

        const result = await sendRequest({
            leagueId,
            action: "resizeLeague",
            totalTeams
        });

        setMessage(result.message);

        await loadTeams(true);
    } catch (requestError) {
        setMessage(
            requestError instanceof Error
                ? requestError.message
                : "No fue posible actualizar los participantes."
        );
    } finally {
        setSaving(false);
    }
}

async function sendRequest(
    body: Record<string, unknown>
): Promise<{
    success: boolean;
    message: string;
}> {
    const response = await fetch(
        COMMISSIONER_CONTROL_URL,
        {
            method: "POST",
            credentials: "include",
            cache: "no-store",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json"
            },
            body: JSON.stringify(body)
        }
    );

    const responseText = await response.text();

    let parsedResult: {
        success?: boolean;
        message?: unknown;
    };

    try {
        parsedResult = JSON.parse(responseText) as {
            success?: boolean;
            message?: unknown;
        };
    } catch {
        console.error(
            "Respuesta no válida del servidor:",
            {
                status: response.status,
                url: response.url,
                body: responseText
            }
        );

        throw new Error(
            `El servidor no devolvió JSON (HTTP ${response.status}).`
        );
    }

    const result = {
        success: parsedResult.success === true,
        message:
            typeof parsedResult.message === "string" &&
            parsedResult.message.trim() !== ""
                ? parsedResult.message
                : "El servidor no devolvió un mensaje válido."
    };

    if (!response.ok || !result.success) {
        throw new Error(result.message);
    }

    return result;
}

    async function saveTeamName() {
        if (!selectedTeam || saving) {
            return;
        }

        const normalizedName = editName.trim();

        if (normalizedName.length < 2) {
            setMessage(
                "El nombre del equipo debe tener al menos 2 caracteres."
            );

            return;
        }

        try {
            setSaving(true);
            setMessage("");

            const result = await sendRequest({
                leagueId,
                action: "rename",
                teamId: selectedTeam.id,
                teamName: normalizedName
            });

            setMessage(result.message);
            await loadTeams(true);

            setSelectedTeam(function (previousTeam) {
                if (!previousTeam) {
                    return null;
                }

                return {
                    ...previousTeam,
                    teamName: normalizedName
                };
            });
        } catch (requestError) {
            setMessage(
                requestError instanceof Error
                    ? requestError.message
                    : "No fue posible actualizar el nombre."
            );
        } finally {
            setSaving(false);
        }
    }

    function requestReplaceOwner() {
        if (!selectedTeam || saving) {
            return;
        }

        if (selectedTeam.id === getAccessToken()) {
            setMessage(
                "La transferencia del equipo del comisionado debe realizarse desde una función especial."
            );

            return;
        }

        if (!newOwnerId.trim() || !newManagerName.trim()) {
            setMessage(
                "Indica el ID y el nombre del nuevo manager."
            );

            return;
        }

        setShowReplaceAlert(true);
    }

    async function replaceOwner() {
        if (!selectedTeam || saving) {
            return;
        }

        try {
            setSaving(true);
            setMessage("");

            const result = await sendRequest({
                leagueId,
                action: "replaceOwner",
                teamId: selectedTeam.id,
                teamName: editName.trim(),
                newOwnerId: newOwnerId.trim(),
                newManagerName: newManagerName.trim()
            });

            setMessage(result.message);
            closeEditor();
            await loadTeams(true);
        } catch (requestError) {
            setMessage(
                requestError instanceof Error
                    ? requestError.message
                    : "No fue posible transferir el equipo."
            );
        } finally {
            setSaving(false);
        }
    }

    function requestRoleChange() {
    if (!selectedTeam || saving) {
        return;
    }

    const currentRole = isCommissionerRole(selectedTeam.role)
        ? "comisioner"
        : "user";

    if (currentRole === selectedRole) {
        setMessage("Este participante ya tiene ese rol.");
        return;
    }

    setShowRoleAlert(true);
}

async function changeTeamRole() {
    if (!selectedTeam || saving) {
        return;
    }

    try {
        setSaving(true);
        setMessage("");

        const result = await sendRequest({
            leagueId,
            action: "changeRole",
            teamId: selectedTeam.id,
            newRole: selectedRole
        });

        setMessage(result.message);

        setSelectedTeam(function (previousTeam) {
            if (!previousTeam) {
                return null;
            }

            return {
                ...previousTeam,
                role: selectedRole
            };
        });

        await loadTeams(true);
    } catch (requestError) {
        setMessage(
            requestError instanceof Error
                ? requestError.message
                : "No fue posible actualizar el rol."
        );
    } finally {
        setSaving(false);
    }
}

const minimumParticipantCount = Math.max(
    MIN_LEAGUE_TEAMS,
    teams.length
);

const currentConfiguredParticipants = Math.max(
    minimumParticipantCount,
    Number(leagueInfo?.teams) || minimumParticipantCount
);

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="commissioner-teams-toolbar">
                    <IonButtons slot="start">
                        <IonBackButton
                            defaultHref={`/fantasySettings/${leagueId}`}
                            text=""
                        />
                    </IonButtons>

                    <IonTitle>Editar equipos</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent
                fullscreen
                className="commissioner-teams-content"
            >
                <IonRefresher
                    slot="fixed"
                    onIonRefresh={handleRefresh}
                >
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando equipos..."
                    />
                </IonRefresher>

                <section className="commissioner-teams-hero">
                    <span className="commissioner-teams-hero__icon">
                        <IonIcon icon={peopleOutline} />
                    </span>

                    <div>
                        <span>ADMINISTRACIÓN DE EQUIPOS</span>
                        <h1>Gestiona los participantes</h1>
                        <p>
                            Puedes cambiar nombres o transferir un equipo
                            conservando su estado actual.
                        </p>
                    </div>
                </section>

                {loading && (
                    <IonProgressBar
                        type="indeterminate"
                        className="commissioner-teams-progress"
                    />
                )}

                {!loading && error && (
                    <section className="commissioner-teams-empty">
                        <strong>No fue posible cargar los equipos</strong>
                        <span>{error}</span>

                        <IonButton
                            size="small"
                            fill="outline"
                            onClick={function () {
                                loadTeams(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </section>
                )}

                {!loading && !error && (
                    <main className="commissioner-teams-main">
                        {!selectedTeam && (
                            <>
                            {!draftHasStarted && (
    <section className="commissioner-participants">
        <div>
            <span>CONFIGURACIÓN PRE-DRAFT</span>
            <h2>Participantes de la liga</h2>
            <p>
                Hay {teams.length} participantes registrados. Puedes
                aumentar el total o reducirlo sin bajar de los equipos
                ya registrados.
            </p>
        </div>

        <IonList inset>
            <IonItem>
                <IonLabel>Total de participantes</IonLabel>

                <IonSelect
                    value={configuredTeams}
                    interface="action-sheet"
                    disabled={saving}
onIonChange={function (event) {
    setConfiguredTeams(
        String(
            event.detail.value ||
            minimumParticipantCount
        )
    );
}}
                >
{Array.from(
    {
        length: MAX_LEAGUE_TEAMS - minimumParticipantCount + 1
    },
    function (_, index) {
        const total = minimumParticipantCount + index;

        return (
            <IonSelectOption
                key={total}
                value={String(total)}
            >
                {total} participantes
            </IonSelectOption>
        );
    }
)}
                </IonSelect>
            </IonItem>
        </IonList>

        <IonNote color="medium">
            Al modificar el total se regenerarán el calendario y
            el orden del draft. Los equipos registrados se conservarán.
        </IonNote>

{message && (
    <IonText
        color={
            message.includes("actualizados") ||
            message.includes("correctamente")
                ? "success"
                : "danger"
        }
    >
        <p className="commissioner-editor__message">
            {message}
        </p>
    </IonText>
)}

        <IonButton
            expand="block"
            color="success"
disabled={
    saving ||
    Number(configuredTeams) ===
        currentConfiguredParticipants
}
            onClick={requestParticipantsChange}
        >
            <IonIcon slot="start" icon={peopleOutline} />
            Actualizar participantes
        </IonButton>
    </section>
)}
                                <section className="commissioner-teams-intro">
                                    <span>{teams.length} EQUIPOS</span>
                                    <h2>Selecciona un equipo</h2>
                                    <p>
                                        El reemplazo conserva roster,
                                        calendario, posición y resultados.
                                    </p>
                                </section>

                                <section className="commissioner-teams-list">
                                    {teams.map(function (team) {
                                        const isCommissioner =
                                            isCommissionerRole(team.user);

                                        return (
                                            <button
                                                key={team.id}
                                                type="button"
                                                className="commissioner-team-card"
                                                onClick={function () {
                                                    selectTeam(team);
                                                }}
                                            >
                                                <span className="commissioner-team-card__avatar">
                                                    {team.teamName1
                                                        .slice(0, 1)
                                                        .toUpperCase()}
                                                </span>

                                                <span className="commissioner-team-card__content">
                                                    <strong>
                                                        {team.teamName1}
                                                    </strong>

                                                    <small>
                                                        {team.manager}
                                                    </small>

                                                    {isCommissioner && (
                                                        <em>
                                                            Comisionado
                                                        </em>
                                                    )}
                                                </span>

                                                <IonIcon
                                                    icon={createOutline}
                                                />
                                            </button>
                                        );
                                    })}
                                </section>
                            </>
                        )}

                        {selectedTeam && (
                            <section className="commissioner-editor">
                                <div className="commissioner-editor__heading">
                                    <span>
                                        EDITANDO EQUIPO
                                    </span>

                                    <h2>{selectedTeam.teamName}</h2>

                                    <p>
                                        Manager actual:{" "}
                                        <strong>
                                            {selectedTeam.manager}
                                        </strong>
                                    </p>
                                </div>

                                <IonList inset>
                                    <IonItem>
                                        <IonLabel position="stacked">
                                            Nombre del equipo
                                        </IonLabel>

                                        <IonInput
    value={editName}
    maxlength={40}
    placeholder="Nombre del equipo"
    onIonInput={function (event) {
        const input =
            event.currentTarget as HTMLIonInputElement;

        setEditName(
    input.value === null ||
    input.value === undefined
        ? ""
        : String(input.value)
);

    }}
/>

                                    </IonItem>
                                </IonList>

                                <IonButton
                                    expand="block"
                                    disabled={saving}
                                    onClick={saveTeamName}
                                >
                                    {saving ? (
                                        <IonSpinner name="crescent" />
                                    ) : (
                                        <IonIcon
                                            slot="start"
                                            icon={saveOutline}
                                        />
                                    )}

                                    Guardar nombre
                                </IonButton>

<section className="commissioner-role">
    <div>
        <span>PERMISOS DE LIGA</span>
        <h3>Rol del participante</h3>
        <p>
            Los comisionados pueden administrar reglas, equipos y
            participantes de esta liga.
        </p>
    </div>

    <IonList inset>
        <IonItem>
            <IonLabel>Tipo de acceso</IonLabel>

            <IonSelect
                value={selectedRole}
                interface="action-sheet"
                disabled={saving}
                onIonChange={function (event) {
                    setSelectedRole(
                        event.detail.value === "comisioner"
                            ? "comisioner"
                            : "user"
                    );
                }}
            >
                <IonSelectOption value="user">
                    Manager
                </IonSelectOption>

                <IonSelectOption value="comisioner">
                    Comisionado
                </IonSelectOption>
            </IonSelect>
        </IonItem>
    </IonList>

    <IonButton
        expand="block"
        fill="outline"
        color="success"
        disabled={saving}
        onClick={requestRoleChange}
    >
        <IonIcon
            slot="start"
            icon={shieldCheckmarkOutline}
        />
        Actualizar permisos
    </IonButton>
</section>

                                <section className="commissioner-transfer">
                                    <div>
                                        <span>REEMPLAZAR MANAGER</span>
                                        <h3>
                                            Transferir equipo conservando
                                            su estado
                                        </h3>
                                        <p>
                                            El nuevo participante recibe el
                                            roster, resultados, calendario y
                                            posición actuales.
                                        </p>
                                    </div>

                                    <IonList inset>
                                        <IonItem>
                                            <IonLabel position="stacked">
                                                ID del nuevo usuario
                                            </IonLabel>

                                            <IonInput
    value={newOwnerId}
    placeholder="Ejemplo: 614c71d411a9d"
onIonInput={function (event) {
    const input =
        event.currentTarget as HTMLIonInputElement;

    setNewOwnerId(
        input.value === null ||
        input.value === undefined
            ? ""
            : String(input.value)
    );
}}

/>

                                        </IonItem>

                                        <IonItem>
                                            <IonLabel position="stacked">
                                                Nombre del nuevo manager
                                            </IonLabel>

                                            <IonInput
    value={newManagerName}
    placeholder="Nombre visible"
    maxlength={60}
onIonInput={function (event) {
    const input =
        event.currentTarget as HTMLIonInputElement;

    setNewManagerName(
        input.value === null ||
        input.value === undefined
            ? ""
            : String(input.value)
    );
}}
/>

                                        </IonItem>
                                    </IonList>

                                    <IonNote color="danger">
                                        Esta acción reemplaza el ID del
                                        equipo anterior en toda la liga.
                                    </IonNote>

                                    <IonButton
                                        expand="block"
                                        color="danger"
                                        disabled={
                                            saving ||
                                            selectedTeam.id ===
                                                getAccessToken()
                                        }
                                        onClick={requestReplaceOwner}
                                    >
                                        <IonIcon
                                            slot="start"
                                            icon={swapHorizontalOutline}
                                        />
                                        Transferir equipo
                                    </IonButton>
                                </section>

                                {message && (
                                    <IonText
                                        color={
                                            message.includes(
                                                "correctamente"
                                            ) ||
                                            message.includes(
                                                "actualizado"
                                            )
                                                ? "success"
                                                : "danger"
                                        }
                                    >
                                        <p className="commissioner-editor__message">
                                            {message}
                                        </p>
                                    </IonText>
                                )}

                                <IonButton
                                    expand="block"
                                    fill="clear"
                                    color="medium"
                                    disabled={saving}
                                    onClick={closeEditor}
                                >
                                    Cancelar
                                </IonButton>
                            </section>
                        )}
                    </main>
                )}
            </IonContent>

            <IonAlert
                isOpen={showReplaceAlert}
                header="¿Transferir este equipo?"
                message={
                    selectedTeam
                        ? `El equipo "${selectedTeam.teamName}" conservará su roster, calendario, resultados y posición. El manager anterior perderá el acceso.`
                        : ""
                }
                buttons={[
                    {
                        text: "Cancelar",
                        role: "cancel"
                    },
                    {
                        text: "Transferir",
                        role: "destructive",
                        handler: function () {
                            replaceOwner();
                        }
                    }
                ]}
                onDidDismiss={function () {
                    setShowReplaceAlert(false);
                }}
            />
<IonAlert
    isOpen={showParticipantsAlert}
    header="¿Actualizar participantes?"
    message={
        `La liga quedará configurada para ${configuredTeams} participantes. ` +
        "Se regenerarán el calendario y el draft. Los equipos ya registrados conservarán su lugar."
    }
    buttons={[
        {
            text: "Cancelar",
            role: "cancel"
        },
        {
            text: "Actualizar",
            handler: function () {
                saveParticipantsChange();
            }
        }
    ]}
    onDidDismiss={function () {
        setShowParticipantsAlert(false);
    }}
/>

            <IonAlert
    isOpen={showRoleAlert}
    header={
        selectedRole === "comisioner"
            ? "¿Asignar como comisionado?"
            : "¿Remover permisos de comisionado?"
    }
    message={
        selectedRole === "comisioner"
            ? "Este manager podrá administrar la liga junto con los demás comisionados."
            : "Este participante conservará su equipo, roster, puntos y posición, pero dejará de tener permisos administrativos."
    }
    buttons={[
        {
            text: "Cancelar",
            role: "cancel"
        },
        {
            text: "Confirmar",
            handler: function () {
                changeTeamRole();
            }
        }
    ]}
    onDidDismiss={function () {
        setShowRoleAlert(false);
    }}
/>
        </IonPage>
    );
};

export default FantasyCommissionerTeamSettings;
